import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiBuildingBold,
    PiCalendarCheckBold,
    PiCurrencyDollarSimpleBold,
    PiFloppyDiskBold,
    PiMapPinBold,
    PiNotePencilBold,
    PiPhoneBold,
    PiUserBold,
} from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type DC = {
    id: number;
    name: string;
    slug: string;
    code: string | null;
    address_line: string | null;
    district: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    responsible: string | null;
    notes: string | null;
    free_storage_days: number;
    daily_rate_brl: number;
    active: boolean;
    sort_order: number;
};

type FormState = {
    name: string;
    slug: string;
    code: string;
    address_line: string;
    district: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    responsible: string;
    notes: string;
    free_storage_days: string;
    daily_rate_brl: string;
    active: boolean;
    sort_order: string;
};

const EMPTY: FormState = {
    name: '',
    slug: '',
    code: '',
    address_line: '',
    district: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    responsible: '',
    notes: '',
    free_storage_days: '30',
    daily_rate_brl: '50',
    active: true,
    sort_order: '0',
};

function slugify(s: string) {
    return s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

const inp =
    'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200';

export default function DistributionCenterFormPage() {
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [slugTouched, setSlugTouched] = useState(false);

    const dcQuery = useQuery({
        queryKey: ['distribution-centers', id, 'edit'],
        enabled: isEdit,
        queryFn: async (): Promise<DC> => {
            const { data } = await api.get(`/distribution-centers/${id}`);
            return (data.data ?? data) as DC;
        },
    });

    useEffect(() => {
        if (!dcQuery.data) return;
        const editing = dcQuery.data;
        setForm({
            name: editing.name ?? '',
            slug: editing.slug ?? '',
            code: editing.code ?? '',
            address_line: editing.address_line ?? '',
            district: editing.district ?? '',
            city: editing.city ?? '',
            state: editing.state ?? '',
            zip: editing.zip ?? '',
            phone: editing.phone ?? '',
            responsible: editing.responsible ?? '',
            notes: editing.notes ?? '',
            free_storage_days: String(editing.free_storage_days ?? 30),
            daily_rate_brl: String(editing.daily_rate_brl ?? 50),
            active: editing.active,
            sort_order: String(editing.sort_order ?? 0),
        });
        setSlugTouched(true);
    }, [dcQuery.data]);

    const set = (k: keyof FormState, v: string | boolean) => {
        setForm((f) => {
            const next = { ...f, [k]: v };
            if (k === 'name' && !slugTouched) {
                next.slug = slugify(v as string);
            }
            return next;
        });
        setErrors((e) => {
            const n = { ...e };
            delete n[k];
            return n;
        });
    };

    const { mutate, isPending } = useMutation({
        mutationFn: async () => {
            const payload = {
                ...form,
                free_storage_days: Number(form.free_storage_days) || 30,
                daily_rate_brl: Number(form.daily_rate_brl) || 50,
                sort_order: Number(form.sort_order) || 0,
            };
            if (isEdit) {
                return api.patch(`/distribution-centers/${id}`, payload);
            }
            return api.post('/distribution-centers', payload);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['distribution-centers'] });
            navigate('/settings/distribution-centers');
        },
        onError: (err: unknown) => {
            const resp = (err as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data;
            if (resp?.errors) {
                const flat: Record<string, string> = {};
                for (const [k, v] of Object.entries(resp.errors)) flat[k] = v[0];
                setErrors(flat);
            }
        },
    });

    const field = (
        k: keyof FormState,
        label: string,
        opts?: { required?: boolean; placeholder?: string; mono?: boolean; type?: string },
    ) => (
        <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                {label}{opts?.required && ' *'}
            </label>
            <input
                type={opts?.type ?? 'text'}
                className={cn(inp, opts?.mono && 'font-mono', errors[k] && 'border-red-400')}
                value={form[k] as string}
                onChange={(e) => set(k, e.target.value)}
                placeholder={opts?.placeholder}
            />
            {errors[k] && <p className="text-xs text-red-500">{errors[k]}</p>}
        </div>
    );

    if (isEdit && dcQuery.isLoading) {
        return <LoadingState variant="detail" />;
    }

    if (isEdit && dcQuery.isError) {
        return (
            <div className="space-y-4">
                <PageHeader
                    title="Editar CD"
                    breadcrumb={[
                        { name: 'Configurações' },
                        { name: 'Centros de Distribuição', href: '/settings/distribution-centers' },
                        { name: 'Editar' },
                    ]}
                />
                <p className="text-sm text-red-600">Não foi possível carregar o centro de distribuição.</p>
                <Link to="/settings/distribution-centers" className="text-sm text-primary hover:underline">
                    Voltar à lista
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={isEdit ? 'Editar CD' : 'Novo Centro de Distribuição'}
                breadcrumb={[
                    { name: 'Configurações' },
                    { name: 'Centros de Distribuição', href: '/settings/distribution-centers' },
                    { name: isEdit ? 'Editar' : 'Novo' },
                ]}
            >
                <div className="flex items-center gap-2">
                    <Link to="/settings/distribution-centers">
                        <Button variant="outline" size="sm" as="span">Cancelar</Button>
                    </Link>
                    <Button
                        size="sm"
                        onClick={() => mutate()}
                        isLoading={isPending}
                        disabled={isPending}
                    >
                        <PiFloppyDiskBold className="me-1.5 size-4" />
                        Salvar
                    </Button>
                </div>
            </PageHeader>

            <div className="grid gap-6 lg:grid-cols-2">
                <Section icon={<PiBuildingBold className="size-4" />} title="Identificação">
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="sm:col-span-2">{field('name', 'Nome', { required: true })}</div>
                            {field('code', 'Código', { placeholder: 'CD-SBC', mono: true })}
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                Slug *
                            </label>
                            <input
                                className={cn(inp, 'font-mono', errors.slug && 'border-red-400')}
                                value={form.slug}
                                onChange={(e) => {
                                    setSlugTouched(true);
                                    set('slug', e.target.value);
                                }}
                                placeholder="cd-sao-bernardo"
                            />
                            {errors.slug && <p className="text-xs text-red-500">{errors.slug}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => set('active', !form.active)}
                                className={cn(
                                    'relative shrink-0 rounded-full transition-colors duration-200',
                                    form.active ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-zinc-700',
                                )}
                                style={{ width: 44, height: 24 }}
                            >
                                <span
                                    className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200"
                                    style={{ left: form.active ? 22 : 2 }}
                                />
                            </button>
                            <span className="text-sm text-gray-600 dark:text-zinc-400">
                                {form.active ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                </Section>

                <Section icon={<PiCalendarCheckBold className="size-4" />} title="Política de Armazenagem">
                    <div className="space-y-3">
                        <div className="rounded-xl bg-amber-50/60 px-4 py-3 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                            Após os <strong>dias grátis</strong>, cobra-se R$/m³/dia pelo volume CBM do produto armazenado.
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                    <PiCalendarCheckBold className="mr-1 inline" />Dias grátis
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        className={cn(inp, 'font-mono')}
                                        value={form.free_storage_days}
                                        onChange={(e) => set('free_storage_days', e.target.value)}
                                    />
                                    <span className="shrink-0 text-xs text-gray-400">dias</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                    <PiCurrencyDollarSimpleBold className="mr-1 inline" />Diária por m³
                                </label>
                                <div className="flex items-center gap-2">
                                    <span className="shrink-0 text-xs text-gray-400">R$</span>
                                    <input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        className={cn(inp, 'font-mono')}
                                        value={form.daily_rate_brl}
                                        onChange={(e) => set('daily_rate_brl', e.target.value)}
                                    />
                                    <span className="shrink-0 text-xs text-gray-400">/ m³·dia</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                <PiNotePencilBold className="mr-1 inline" />Observações
                            </label>
                            <textarea
                                rows={3}
                                className={cn(inp, 'resize-none')}
                                value={form.notes}
                                onChange={(e) => set('notes', e.target.value)}
                            />
                        </div>
                    </div>
                </Section>

                <Section icon={<PiMapPinBold className="size-4" />} title="Endereço" className="lg:col-span-2">
                    <div className="space-y-3">
                        {field('address_line', 'Logradouro', { placeholder: 'Rua, Av...' })}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {field('district', 'Bairro')}
                            {field('city', 'Cidade')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                    UF
                                </label>
                                <input
                                    maxLength={2}
                                    className={cn(inp, 'uppercase')}
                                    value={form.state}
                                    onChange={(e) => set('state', e.target.value.toUpperCase())}
                                    placeholder="SP"
                                />
                            </div>
                            {field('zip', 'CEP', { placeholder: '00000-000', mono: true })}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                    <PiPhoneBold className="mr-1 inline" />Telefone
                                </label>
                                <input
                                    className={inp}
                                    value={form.phone}
                                    onChange={(e) => set('phone', e.target.value)}
                                    placeholder="(11) 99999-9999"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                    <PiUserBold className="mr-1 inline" />Responsável
                                </label>
                                <input
                                    className={inp}
                                    value={form.responsible}
                                    onChange={(e) => set('responsible', e.target.value)}
                                    placeholder="Nome do responsável"
                                />
                            </div>
                        </div>
                    </div>
                </Section>
            </div>
        </div>
    );
}

function Section({
    icon,
    title,
    children,
    className,
}: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <section className={cn('rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900', className)}>
            <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3 dark:border-zinc-800">
                <span className="flex size-7 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
                    {icon}
                </span>
                <span className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-zinc-400">
                    {title}
                </span>
            </div>
            {children}
        </section>
    );
}
